'use client';

import { useEffect, useReducer, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import api, { API_ENDPOINTS } from '@/utils/api';
import { getAuthUser, postLogin } from '@/services/auth';
import { setConfiguration } from '@/redux/slices/configurations';
import { AuthUser, User } from '@/types/users';
import { Configuration } from '@/types/configurations';
import { updateProfilePicture, updateUsers } from '@/services/users';

import { AuthContext } from './auth-context';
import { setSession } from './utils';
import { ActionMapType, AuthStateType } from '../../types';

// ----------------------------------------------------------------------
//
// JWT-based auth provider. Phase 7 rewrite.
//
// Before: every method that wasn't login/logout went through
// `internalApi` (Next.js API routes under /api/auth/*) that proxied
// + decoded Xano. Those routes are deleted; everything now hits the
// NestJS backend directly via `api`.
//
// SMS-based OTP flows (forgotPassword by phone, getPasswordChangeToken
// by phone) have been removed — the v2 backend is email-only (Twilio
// dropped, plan §6.3). The phone-shaped methods stay as no-op stubs
// so existing UI components that destructure them keep compiling;
// they'll get cleaned up as those screens are refactored.
//
// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  UPDATE = 'UPDATE',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: { user: AuthUser };
  [Types.LOGIN]: { user: AuthUser };
  [Types.REGISTER]: { user: AuthUser };
  [Types.UPDATE]: { user: AuthUser };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = { user: null, loading: true };

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) return { loading: false, user: action.payload.user };
  if (action.type === Types.LOGIN) return { ...state, user: action.payload.user };
  if (action.type === Types.REGISTER) return { ...state, user: action.payload.user };
  if (action.type === Types.UPDATE) {
    return { ...state, user: { ...(state.user! || {}), ...action.payload.user! } };
  }
  if (action.type === Types.LOGOUT) return { ...state, user: null };
  return state;
};

// ----------------------------------------------------------------------

type Props = {
  initialUser: AuthUser;
  initialConfiguration: Configuration | null;
  children: React.ReactNode;
};

export function AuthProvider({ initialUser, initialConfiguration, children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const dispatchRedux = useDispatch();

  const initialize = useCallback(() => {
    dispatchRedux(setConfiguration(initialConfiguration));
    dispatch({ type: Types.INITIAL, payload: { user: initialUser } });
  }, [dispatchRedux, initialConfiguration, initialUser]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const { accessToken } = await postLogin(email, password);
    setSession(accessToken);
    const user = await getAuthUser();
    dispatch({ type: Types.LOGIN, payload: { user } });
  }, []);

  // Forgot password — email only. SMS variant kept as a stub so older
  // screens that wire it up still compile.
  const forgotPasswordEmail = useCallback(
    async (email: string) =>
      api.post(API_ENDPOINTS.auth.forgotPassword, { params: { email } }),
    []
  );
  const forgotPassword = useCallback(async () => {
    throw new Error('Password reset by SMS is no longer supported. Use email.');
  }, []);

  // Forgot-password flow has three screens (email → code → new password).
  // The backend used to expose a 3-step endpoint set; v2 collapsed
  // verification + new-password into one atomic call
  // (POST /auth/reset-password). To avoid breaking the screen ordering,
  // the "verify code" step now NOOPs (there's no API to validate without
  // consuming the OTP) — it just forwards the code via URL params to
  // the change-password screen which submits everything together.
  const getPasswordChangeTokenEmail = useCallback(async (code: number, email: string) => {
    // Pretend we minted a token; verify-view stuffs it back into the
    // URL and change-password-view reads `code` from there.
    return { authToken: String(code), user: email };
  }, []);
  const getPasswordChangeToken = useCallback(async () => {
    throw new Error('Password reset by SMS is no longer supported. Use email.');
  }, []);

  /** Anonymous reset (forgot flow). Calls POST /auth/reset-password
   *  with email + code + new password atomically. Backend returns 204. */
  const resetPassword = useCallback(
    async (email: string, code: string, newPassword: string) =>
      api.post(API_ENDPOINTS.auth.resetPassword, {
        params: { email, code, newPassword },
      }),
    []
  );

  /** Authenticated change-password. Backend requires the current
   *  password too so we ask for both on the form. user_id is kept in
   *  the signature for callers that still pass it; the backend infers
   *  the user from the bearer token (it's a "change MY password" op). */
  const changePassword = useCallback(
    async (_user_id: number, newPassword: string, currentPassword: string) =>
      api.post(API_ENDPOINTS.auth.changePassword, {
        params: { currentPassword, newPassword },
      }),
    []
  );

  // REGISTER — direct POST /auth/register. The backend's saga handles
  // Hikvision person + Scanntech client + welcome email server-side.
  const register = useCallback(
    (user: Partial<User>) => api.post(API_ENDPOINTS.auth.register, { params: user }),
    []
  );

  // UPDATE
  const update = useCallback(async (id: number, user: Partial<User>) => {
    const updatedUser = await updateUsers(id, {
      enabled: user.enabled === undefined ? true : user.enabled,
      ...user,
    });
    dispatch({ type: Types.UPDATE, payload: { user: updatedUser } });
  }, []);

  const updatePicture = useCallback(async (image: string) => {
    const updatedUser = await updateProfilePicture(image);
    dispatch({ type: Types.UPDATE, payload: { user: updatedUser } });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    // Best-effort backend invalidation; ignore errors so we always
    // clear local state even if the backend is unreachable.
    await api.post(API_ENDPOINTS.auth.logout).catch(() => undefined);
    setSession(null);
    dispatch({ type: Types.LOGOUT });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';
  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      forgotPassword,
      forgotPasswordEmail,
      getPasswordChangeToken,
      getPasswordChangeTokenEmail,
      changePassword,
      resetPassword,
      register,
      logout,
      update,
      updatePicture,
    }),
    [
      changePassword,
      resetPassword,
      forgotPassword,
      forgotPasswordEmail,
      getPasswordChangeToken,
      getPasswordChangeTokenEmail,
      login,
      logout,
      register,
      state.user,
      status,
      update,
      updatePicture,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
